import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, InfoNote } from "@/components/shared";
import {
  ArrowRight, BarChart3, Check, CheckCircle2, Database, Github, Globe,
  Loader2, LockKeyhole, PlugZap, RefreshCw, Rocket, Search, ShieldCheck,
  Sparkles, Unplug, X,
} from "lucide-react";
import { toast } from "sonner";

const ScopeBadge = ({ scope }) => (
  <Badge variant="outline" className="h-5 bg-muted/40 px-1.5 text-[10px] font-medium text-muted-foreground">
    {scope}
  </Badge>
);

const ConnectionCard = ({ icon: Icon, title, description, connected, scope, children, testId }) => (
  <Card className="overflow-hidden shadow-none" data-testid={testId}>
    <CardHeader className="border-b bg-muted/20 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${connected ? "border-emerald-200 bg-emerald-50" : "bg-card"}`}>
          <Icon className={`h-4 w-4 ${connected ? "text-emerald-700" : "text-primary"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
            <ScopeBadge scope={scope} />
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
        <Badge variant="outline" className={`h-6 shrink-0 px-2 text-[11px] ${connected ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
          {connected ? "Connected" : "Not connected"}
        </Badge>
      </div>
    </CardHeader>
    <CardContent className="space-y-3 p-4 sm:p-5">{children}</CardContent>
  </Card>
);

const Unavailable = ({ provider }) => (
  <InfoNote>
    {provider} is not available on this deployment yet. The SIX9 platform team needs to enable it; no setup is required from workspace users.
  </InfoNote>
);

const AccountLine = ({ label, value }) => (
  <div className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs">
    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
    <span className="text-muted-foreground">{label}</span>
    <span className="truncate font-medium">{value}</span>
  </div>
);

export default function Connections() {
  const navigate = useNavigate();
  const { activeWebsite, activeWorkspace, loadWorkspaces } = useApp();
  const [params] = useSearchParams();
  const [google, setGoogle] = useState(null);
  const [googleCfg, setGoogleCfg] = useState(null);
  const [ai, setAi] = useState(null);
  const [gh, setGh] = useState(null);
  const [ghCfg, setGhCfg] = useState(null);
  const [deploy, setDeploy] = useState(null);
  const [conversionConfig, setConversionConfig] = useState({ mappings: [], suggested_events: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [aiKey, setAiKey] = useState("");
  const [conversionEvent, setConversionEvent] = useState("");
  const [gscProps, setGscProps] = useState(null);
  const [ga4Props, setGa4Props] = useState(null);
  const [repos, setRepos] = useState(null);
  const [vercelToken, setVercelToken] = useState("");
  const [projects, setProjects] = useState(null);
  const [disconnect, setDisconnect] = useState(null);

  const load = async () => {
    if (!activeWorkspace) { setLoading(false); return; }
    setLoading(true);
    try {
      const calls = [
        api.get("/integrations/google/config-status"),
        api.get(`/workspaces/${activeWorkspace.id}/ai`),
        api.get("/integrations/github/config-status"),
        api.get(`/workspaces/${activeWorkspace.id}/github/status`),
        api.get(`/workspaces/${activeWorkspace.id}/deploy/status`),
      ];
      if (activeWebsite) {
        calls.push(api.get(`/websites/${activeWebsite.id}/google/status`));
        calls.push(api.get(`/websites/${activeWebsite.id}/ga4/conversion-mappings`));
      }
      const [cfg, aiR, ghCfgR, ghR, depR, gR, conversionR] = await Promise.all(calls);
      setGoogleCfg(cfg.data);
      setAi(aiR.data);
      setGhCfg(ghCfgR.data);
      setGh(ghR.data);
      setDeploy(depR.data);
      setGoogle(gR?.data || null);
      setConversionConfig(conversionR?.data || { mappings: [], suggested_events: [] });
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeWorkspace?.id, activeWebsite?.id]); // eslint-disable-line

  useEffect(() => {
    if (params.get("google") === "connected") toast.success("Google account connected. Choose your Search Console property next.");
    if (params.get("google_error")) toast.error(`Google connection failed: ${params.get("google_error")}`);
    if (params.get("github") === "connected") toast.success("GitHub connected. Link a repository to this website next.");
    if (params.get("github_error")) toast.error(`GitHub connection failed: ${params.get("github_error")}`);
  }, [params]);

  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const requestDisconnect = (key, provider, action) => setDisconnect({ key, provider, action });
  const confirmDisconnect = async () => {
    const pending = disconnect;
    setDisconnect(null);
    if (pending) await run(pending.key, pending.action, `${pending.provider} disconnected`);
  };

  const connectGoogle = () => run("google", async () => {
    const { data } = await api.post(`/websites/${activeWebsite.id}/google/authorize`);
    window.location.assign(data.authorization_url);
  });

  const connectGithub = () => run("gh", async () => {
    const { data } = await api.post(`/workspaces/${activeWorkspace.id}/github/authorize`);
    window.location.assign(data.authorization_url);
  });

  const loadGscProps = () => run("gscprops", async () => {
    const { data } = await api.get(`/websites/${activeWebsite.id}/gsc/properties`);
    setGscProps(data.properties);
  });

  const loadGa4Props = () => run("ga4props", async () => {
    const { data } = await api.get(`/websites/${activeWebsite.id}/ga4/properties`);
    setGa4Props(data.properties);
  });

  const saveMappings = (mappings, success) => run("conversions", async () => {
    await api.put(`/websites/${activeWebsite.id}/ga4/conversion-mappings`, { mappings });
    setConversionEvent("");
  }, success);

  const conn = google?.connection;
  const coreSteps = useMemo(() => [
    { label: "Google account", done: !!google?.connected },
    { label: "Search Console property", done: !!conn?.gsc_property },
    { label: "First data sync", done: !!conn?.gsc_sync?.lastSuccessfulSync },
  ], [google, conn]);
  const completedCore = coreSteps.filter((step) => step.done).length;
  const connectedCount = [conn?.gsc_property, conn?.ga4_property, ai?.connected, gh?.connected, deploy?.connected].filter(Boolean).length;

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-32 w-full" /><div className="grid gap-4 lg:grid-cols-2"><Skeleton className="h-72" /><Skeleton className="h-72" /></div></div>;
  }

  if (!activeWebsite) {
    return (
      <EmptyState icon={Globe} title="Add a website before connecting data" message="Connections are attached to a website so every imported property and metric stays isolated.">
        <Button onClick={() => navigate("/onboarding?step=website")}>Add website</Button>
      </EmptyState>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight">Connections</h1>
            <Badge variant="outline" className="bg-card text-xs">{connectedCount}/5 ready</Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Manage data and automation for <span className="font-medium text-foreground">{activeWebsite.domain}</span> in {activeWorkspace?.name}.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="connections-refresh-button">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh status
        </Button>
      </div>

      <Card className="border-primary/20 bg-primary/[0.025] shadow-none">
        <CardContent className="grid gap-5 p-4 sm:p-5 lg:grid-cols-[1fr_260px] lg:items-center">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold"><PlugZap className="h-4 w-4 text-primary" /> Recommended data setup</div>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">Connect the Google account that can access this domain, choose the matching property, then run the first sync. AI, GitHub and deployment connections can be added later.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {coreSteps.map((step, index) => (
                <div key={step.label} className="flex items-center gap-2 text-xs">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border font-semibold ${step.done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "bg-card text-muted-foreground"}`}>
                    {step.done ? <Check className="h-3.5 w-3.5" /> : index + 1}
                  </span>
                  <span className={step.done ? "font-medium text-foreground" : "text-muted-foreground"}>{step.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-md border bg-card p-3">
            <div className="flex items-center justify-between text-xs"><span className="font-medium">Data readiness</span><span className="tabular-nums text-muted-foreground">{completedCore}/3</span></div>
            <Progress value={(completedCore / 3) * 100} className="mt-2 h-1.5" />
            <div className="mt-3">
              {!google?.connected && googleCfg?.configured && <Button size="sm" className="w-full" onClick={connectGoogle}>Connect Google <ArrowRight className="ml-auto h-3.5 w-3.5" /></Button>}
              {google?.connected && !conn?.gsc_property && <Button size="sm" className="w-full" variant="outline" onClick={loadGscProps}>Choose property <ArrowRight className="ml-auto h-3.5 w-3.5" /></Button>}
              {conn?.gsc_property && !conn?.gsc_sync?.lastSuccessfulSync && <Button size="sm" className="w-full" onClick={() => run("gscsync", () => api.post(`/websites/${activeWebsite.id}/gsc/sync`), "Search Console sync started")}>Run first sync <ArrowRight className="ml-auto h-3.5 w-3.5" /></Button>}
              {completedCore === 3 && <div className="flex items-center gap-2 text-xs font-medium text-emerald-700"><ShieldCheck className="h-4 w-4" /> Search data is ready</div>}
              {!googleCfg?.configured && <div className="text-xs text-muted-foreground">Google is temporarily unavailable.</div>}
            </div>
          </div>
        </CardContent>
      </Card>

      <section aria-labelledby="data-connections-heading" className="space-y-3">
        <div>
          <h2 id="data-connections-heading" className="text-sm font-semibold">SEO data</h2>
          <p className="text-xs text-muted-foreground">Website-level sources used for search performance, traffic and conversion reporting.</p>
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          <ConnectionCard icon={Search} title="Google Search Console" description="Queries, clicks, impressions, positions and page performance." connected={!!conn?.gsc_property} scope="Website" testId="connections-gsc-card">
            {!googleCfg?.configured ? <Unavailable provider="Google" /> : !google?.connected ? (
              <div className="space-y-3">
                <InfoNote>You will be redirected to Google to grant read-only Search Console and Analytics access. SIX9 never receives your Google password.</InfoNote>
                <Button size="sm" onClick={connectGoogle} disabled={busy === "google"} data-testid="connections-gsc-connect-button">
                  {busy === "google" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Connect Google account
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <AccountLine label="Google account" value={conn?.google_email || "Connected account"} />
                {conn?.gsc_property ? (
                  <div className="space-y-2">
                    <AccountLine label="Property" value={conn.gsc_property} />
                    <div className="grid gap-2 rounded-md border p-3 text-xs sm:grid-cols-2">
                      <div><div className="text-muted-foreground">Last successful sync</div><div className="mt-0.5 font-medium">{conn?.gsc_sync?.lastSuccessfulSync ? new Date(conn.gsc_sync.lastSuccessfulSync).toLocaleString() : "Never"}</div></div>
                      <div><div className="text-muted-foreground">Records imported</div><div className="mt-0.5 font-medium tabular-nums">{conn?.gsc_sync?.recordsImported?.toLocaleString() || "—"}</div></div>
                    </div>
                    {conn?.gsc_sync?.syncStatus === "failed" && <InfoNote>The last sync failed. Your existing data is safe; retry when ready.</InfoNote>}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-xs">Search Console property</Label>
                    {!gscProps ? (
                      <Button size="sm" variant="outline" onClick={loadGscProps} disabled={busy === "gscprops"} data-testid="connections-gsc-load-properties">
                        {busy === "gscprops" && <Loader2 className="mr-2 h-3 w-3 animate-spin" />} Load available properties
                      </Button>
                    ) : gscProps.length ? (
                      <Select onValueChange={(v) => run("gscsel", () => api.post(`/websites/${activeWebsite.id}/gsc/property`, { siteUrl: v }), "Search Console property selected")}>
                        <SelectTrigger className="text-xs" data-testid="connections-gsc-property-select"><SelectValue placeholder="Choose a verified property" /></SelectTrigger>
                        <SelectContent>{gscProps.map((p) => <SelectItem key={p.siteUrl} value={p.siteUrl}>{p.siteUrl}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <InfoNote>No Search Console properties were returned for this Google account.</InfoNote>}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  {conn?.gsc_property && <Button size="sm" onClick={() => run("gscsync", () => api.post(`/websites/${activeWebsite.id}/gsc/sync`), "Search Console sync started")} disabled={busy === "gscsync"} data-testid="connections-gsc-sync-button"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync now</Button>}
                  <Button size="sm" variant="outline" onClick={connectGoogle}>Reconnect account</Button>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => requestDisconnect("gdis", "Google", () => api.delete(`/websites/${activeWebsite.id}/google`))} data-testid="connections-google-disconnect"><Unplug className="mr-1.5 h-3.5 w-3.5" /> Disconnect</Button>
                </div>
              </div>
            )}
          </ConnectionCard>

          <ConnectionCard icon={BarChart3} title="Google Analytics 4" description="Organic sessions, users and the conversion events that matter to your business." connected={!!conn?.ga4_property} scope="Website" testId="connections-ga4-card">
            {!googleCfg?.configured ? <Unavailable provider="Google" /> : !google?.connected ? (
              <InfoNote>Connect Google in the Search Console card first. One secure authorization covers both products.</InfoNote>
            ) : (
              <div className="space-y-3">
                {conn?.ga4_property ? <AccountLine label="Property" value={conn.ga4_property} /> : (
                  <div className="space-y-2">
                    <Label className="text-xs">Analytics property</Label>
                    {!ga4Props ? (
                      <Button size="sm" variant="outline" onClick={loadGa4Props} disabled={busy === "ga4props"} data-testid="connections-ga4-load-properties">
                        {busy === "ga4props" && <Loader2 className="mr-2 h-3 w-3 animate-spin" />} Load GA4 properties
                      </Button>
                    ) : ga4Props.length ? (
                      <Select onValueChange={(v) => run("ga4sel", () => api.post(`/websites/${activeWebsite.id}/ga4/property`, { property: v }), "GA4 property selected")}>
                        <SelectTrigger className="text-xs" data-testid="connections-ga4-property-select"><SelectValue placeholder="Choose a GA4 property" /></SelectTrigger>
                        <SelectContent>{ga4Props.map((p) => <SelectItem key={p.property} value={p.property}>{p.displayName} · {p.account}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <InfoNote>No GA4 properties were returned for this Google account.</InfoNote>}
                  </div>
                )}
                {conn?.ga4_property && (
                  <div className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center justify-between"><Label className="text-xs">Conversion events</Label><span className="text-[10px] text-muted-foreground">Optional</span></div>
                    <div className="flex gap-2">
                      <Input list="ga4-events" value={conversionEvent} onChange={(e) => setConversionEvent(e.target.value)} placeholder="e.g. generate_lead" className="h-8 text-xs" />
                      <datalist id="ga4-events">{(conversionConfig.suggested_events || []).map((event) => <option key={event} value={event} />)}</datalist>
                      <Button size="sm" variant="outline" className="h-8" disabled={!conversionEvent.trim() || busy === "conversions"} onClick={() => saveMappings([...(conversionConfig.mappings || []), { event: conversionEvent.trim(), category: "primary" }], "Conversion event added")}>Add</Button>
                    </div>
                    {(conversionConfig.mappings || []).length > 0 && <div className="flex flex-wrap gap-1.5">{conversionConfig.mappings.map((mapping) => <Badge key={mapping.event} variant="secondary" className="gap-1 text-[10px]">{mapping.event}<button aria-label={`Remove ${mapping.event}`} onClick={() => saveMappings(conversionConfig.mappings.filter((x) => x.event !== mapping.event), "Conversion event removed")}><X className="h-3 w-3" /></button></Badge>)}</div>}
                  </div>
                )}
                {conn?.ga4_property && <div className="flex flex-wrap items-center gap-2 border-t pt-3"><Button size="sm" onClick={() => run("ga4sync", () => api.post(`/websites/${activeWebsite.id}/ga4/sync`), "GA4 sync started")} disabled={busy === "ga4sync"} data-testid="connections-ga4-sync-button"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync now</Button><span className="text-[11px] text-muted-foreground">Last sync: {conn?.ga4_sync?.lastSuccessfulSync ? new Date(conn.ga4_sync.lastSuccessfulSync).toLocaleString() : "never"}</span></div>}
              </div>
            )}
          </ConnectionCard>
        </div>
      </section>

      <section aria-labelledby="automation-connections-heading" className="space-y-3">
        <div>
          <h2 id="automation-connections-heading" className="text-sm font-semibold">Automation</h2>
          <p className="text-xs text-muted-foreground">Workspace-level tools used only after a human approves a task.</p>
        </div>
        <div className="grid gap-4 xl:grid-cols-3">
          <ConnectionCard icon={Sparkles} title="OpenAI" description="Bring your own key for scoped change plans and code tasks." connected={!!ai?.connected} scope="Workspace" testId="connections-ai-card">
            {ai?.connected ? (
              <div className="space-y-3">
                <AccountLine label={ai.provider || "Provider"} value={ai.masked_key} />
                <div className="space-y-1.5"><Label className="text-xs">Model</Label><Select value={ai.model || ""} onValueChange={(v) => run("aimodel", () => api.post(`/workspaces/${activeWorkspace.id}/ai/model`, { model: v }), "AI model updated")}><SelectTrigger className="h-8 text-xs" data-testid="connections-ai-model-select"><SelectValue /></SelectTrigger><SelectContent>{(ai.models || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={() => run("aitest", () => api.post(`/workspaces/${activeWorkspace.id}/ai/test`), "OpenAI connection is healthy")} disabled={busy === "aitest"} data-testid="connections-ai-test-button">Test connection</Button><Button size="sm" variant="ghost" className="text-rose-600" onClick={() => requestDisconnect("aidis", "OpenAI", () => api.delete(`/workspaces/${activeWorkspace.id}/ai`))} data-testid="connections-ai-disconnect">Disconnect</Button></div>
                <div className="space-y-1.5 border-t pt-3"><Label htmlFor="replace-ai-key" className="text-xs">Replace API key</Label><div className="flex gap-2"><Input id="replace-ai-key" type="password" value={aiKey} onChange={(e) => setAiKey(e.target.value)} placeholder="sk-..." className="h-8 text-xs" data-testid="connections-ai-replace-input" /><Button size="sm" className="h-8" disabled={!aiKey.trim() || busy === "aikey"} onClick={() => run("aikey", async () => { await api.post(`/workspaces/${activeWorkspace.id}/ai/connect`, { provider: "openai", api_key: aiKey }); setAiKey(""); }, "API key replaced")} data-testid="connections-ai-replace-button">Replace</Button></div></div>
              </div>
            ) : (
              <div className="space-y-3">
                <InfoNote>Your key is validated once, encrypted server-side, and only its masked ending is shown later.</InfoNote>
                <div className="space-y-1.5"><Label htmlFor="ai-key" className="text-xs">OpenAI API key</Label><Input id="ai-key" type="password" autoComplete="off" value={aiKey} onChange={(e) => setAiKey(e.target.value)} placeholder="sk-..." className="h-9 text-xs" data-testid="connections-ai-key-input" /></div>
                <Button size="sm" disabled={!aiKey.trim() || busy === "aikey"} onClick={() => run("aikey", async () => { await api.post(`/workspaces/${activeWorkspace.id}/ai/connect`, { provider: "openai", api_key: aiKey }); setAiKey(""); }, "OpenAI connected")} data-testid="connections-ai-connect-button">{busy === "aikey" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Validate & connect</Button>
              </div>
            )}
          </ConnectionCard>

          <ConnectionCard icon={Github} title="GitHub" description="Create reviewable branches and pull requests; SIX9 never auto-merges." connected={!!gh?.connected} scope="Workspace" testId="connections-github-card">
            {!ghCfg?.configured ? <Unavailable provider="GitHub" /> : !gh?.connected ? (
              <div className="space-y-3"><InfoNote>GitHub will ask which repositories SIX9 can access. You can revoke access at any time.</InfoNote><Button size="sm" onClick={connectGithub} disabled={busy === "gh"} data-testid="connections-github-connect-button">Connect GitHub</Button></div>
            ) : (
              <div className="space-y-3">
                <AccountLine label="Account" value={`@${gh.connection?.login || "connected"}`} />
                {activeWebsite?.github_repo ? <AccountLine label="Repository" value={`${activeWebsite.github_repo.full_name} · ${activeWebsite.github_repo.default_branch}`} /> : (
                  <div className="space-y-2"><Label className="text-xs">Repository for this website</Label>{!repos ? <Button size="sm" variant="outline" onClick={() => run("repos", async () => { const { data } = await api.get(`/workspaces/${activeWorkspace.id}/github/repos`); setRepos(data.repos); })} disabled={busy === "repos"} data-testid="connections-github-load-repos">Load repositories</Button> : <Select onValueChange={(v) => { const repo = repos.find((x) => x.full_name === v); run("reposel", async () => { await api.post(`/websites/${activeWebsite.id}/github/repo`, { full_name: repo.full_name, default_branch: repo.default_branch }); await loadWorkspaces(activeWorkspace.id, activeWebsite.id); }, "Repository linked"); }}><SelectTrigger className="h-8 text-xs" data-testid="connections-github-repo-select"><SelectValue placeholder="Choose repository" /></SelectTrigger><SelectContent>{repos.map((repo) => <SelectItem key={repo.full_name} value={repo.full_name}>{repo.full_name}{repo.private ? " · private" : ""}</SelectItem>)}</SelectContent></Select>}</div>
                )}
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => requestDisconnect("ghdis", "GitHub", () => api.delete(`/workspaces/${activeWorkspace.id}/github`))} data-testid="connections-github-disconnect">Disconnect</Button>
              </div>
            )}
          </ConnectionCard>

          <ConnectionCard icon={Rocket} title="Vercel" description="Find preview deployments for approved pull-request branches." connected={!!deploy?.connected} scope="Workspace" testId="connections-deploy-card">
            {!deploy?.connected ? (
              <div className="space-y-3"><InfoNote>Create a scoped Vercel token, then paste it here. The encrypted token never returns to the browser.</InfoNote><div className="space-y-1.5"><Label htmlFor="vercel-token" className="text-xs">Vercel token</Label><Input id="vercel-token" type="password" autoComplete="off" value={vercelToken} onChange={(e) => setVercelToken(e.target.value)} placeholder="Paste token" className="h-9 text-xs" data-testid="connections-vercel-token-input" /></div><Button size="sm" disabled={!vercelToken.trim() || busy === "vercel"} onClick={() => run("vercel", async () => { await api.post(`/workspaces/${activeWorkspace.id}/deploy/connect`, { provider: "vercel", token: vercelToken }); setVercelToken(""); }, "Vercel connected")} data-testid="connections-vercel-connect-button">Connect Vercel</Button></div>
            ) : (
              <div className="space-y-3">
                <AccountLine label="Account" value={`${deploy.connection?.username || "connected"} · ${deploy.connection?.masked_token || "token saved"}`} />
                {activeWebsite?.deploy_project ? <AccountLine label="Project" value={activeWebsite.deploy_project.project_name} /> : <div className="space-y-2"><Label className="text-xs">Project for this website</Label>{!projects ? <Button size="sm" variant="outline" onClick={() => run("projects", async () => { const { data } = await api.get(`/workspaces/${activeWorkspace.id}/deploy/projects`); setProjects(data.projects); })} disabled={busy === "projects"} data-testid="connections-vercel-load-projects">Load projects</Button> : <Select onValueChange={(v) => { const project = projects.find((x) => x.id === v); run("projsel", async () => { await api.post(`/websites/${activeWebsite.id}/deploy/project`, { project_id: project.id, project_name: project.name }); await loadWorkspaces(activeWorkspace.id, activeWebsite.id); }, "Vercel project linked"); }}><SelectTrigger className="h-8 text-xs" data-testid="connections-vercel-project-select"><SelectValue placeholder="Choose project" /></SelectTrigger><SelectContent>{projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}</SelectContent></Select>}</div>}
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => requestDisconnect("depdis", "Vercel", () => api.delete(`/workspaces/${activeWorkspace.id}/deploy`))} data-testid="connections-deploy-disconnect">Disconnect</Button>
              </div>
            )}
          </ConnectionCard>
        </div>
      </section>

      <section aria-labelledby="sandbox-heading" className="space-y-3">
        <div><h2 id="sandbox-heading" className="text-sm font-semibold">Sandbox</h2><p className="text-xs text-muted-foreground">Explore the product safely before importing real performance data.</p></div>
        <ConnectionCard icon={Database} title="Demo data" description="A clearly labelled synthetic dataset for testing the opportunity engine." connected={activeWebsite?.data_source === "demo"} scope="Website" testId="connections-demo-card">
          <InfoNote>Demo records are automatically removed after the first successful Google sync.</InfoNote>
          <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" disabled={activeWebsite?.data_source === "google" || busy === "demo"} onClick={() => run("demo", () => api.post(`/websites/${activeWebsite.id}/demo-data`), "Demo data loaded")} data-testid="connections-demo-load-button">Load demo data</Button>{activeWebsite?.data_source === "demo" && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => requestDisconnect("demox", "Demo data", () => api.delete(`/websites/${activeWebsite.id}/demo-data`))} data-testid="connections-demo-clear-button">Remove demo data</Button>}</div>
        </ConnectionCard>
      </section>

      <div className="flex items-start gap-2 rounded-md border bg-card p-3 text-xs text-muted-foreground">
        <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        OAuth tokens and API keys are encrypted at rest. Provider passwords are never collected, and automation cannot publish without explicit human approval.
      </div>

      <AlertDialog open={!!disconnect} onOpenChange={(open) => !open && setDisconnect(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Disconnect {disconnect?.provider}?</AlertDialogTitle><AlertDialogDescription>This stops future syncs or automation that depends on this connection. Existing SEO data and task history will stay in your workspace.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep connected</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDisconnect}>Disconnect</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
